import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography, Box, Container, Paper, Button, Grid,
  Card, CardContent, CardActions, CircularProgress, Alert, Chip,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import axios from '../utils/api';

const ServiceProviderDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!email || !token) {
        setLoadError('Email and token are required');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.post('/api/service-providers/verify-edit-token', { email, token });
        setOrganizationName(response.data.provider?.organizationName || '');
      } catch (err: any) {
        setLoadError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to verify token.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [email, token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading your dashboard...</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>
        <Button variant="outlined" href="/register/agency/edit-request">Request a New Token</Button>
      </Container>
    );
  }

  const editFormUrl = `/register/agency/edit-form?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 }, overflow: 'hidden' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {organizationName}
          </Typography>
          <Chip label="Legal Service Provider" color="primary" />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Edit color="primary" />
                  <Typography variant="h6">Edit Our Registration</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Update your organization details and services.
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button variant="contained" fullWidth onClick={() => navigate(editFormUrl)}>
                  Edit Registration
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ServiceProviderDashboard;
